import { useContext, useMemo } from 'react';
import { useQueries, type UseQueryResult } from '@tanstack/react-query';
import type { WorkbookAggregateResult } from '@erp/shared-types';
import { reportAggregateQueryOptions } from './workbookReportDataBatch';
import {
  ReportValuesSelect,
  useDatasetFilterOptions,
  type ReportFilterOptions,
  type ReportValuesControlProps,
} from '@/components/report-canvas/filters/ReportFilterBar';
import { WorkbookReportServerContext } from './workbookReportRegistry';

// Module-level, so the combined result keeps its identity between renders.
function combine(results: UseQueryResult<WorkbookAggregateResult>[]) {
  return {
    rows: results.map((result) => result.data?.rows ?? []),
    isLoading: results.some((result) => result.isLoading),
  };
}

/**
 * A value-list filter's values in a Workbook Report: those of the rows in the
 * browser, and, for a Result past the Client row limit, its column's distinct
 * values from Server aggregation (grouped by the column; at most 5,000).
 */
function useWorkbookFilterOptions(column: string): ReportFilterOptions {
  const local = useDatasetFilterOptions(column);
  const server = useContext(WorkbookReportServerContext);
  const targets = useMemo(
    () =>
      [...(server?.sources.values() ?? [])].filter(
        (source) =>
          source.path === 'server' &&
          source.runId &&
          source.columns.some((c) => c.name === column),
      ),
    [server, column],
  );
  const queries = useQueries({
    queries: targets.map((source) =>
      reportAggregateQueryOptions({
        workbookId: server?.workbookId ?? '',
        runId: source.runId ?? '',
        sheetId: source.sheet.id,
        aggregation: { groupBy: [column], measures: [] },
      }),
    ),
    combine,
  });
  return useMemo(() => {
    const values = new Set(local.values);
    for (const rows of queries.rows)
      for (const [value] of rows)
        values.add(value === null || value === undefined ? '' : String(value));
    const collator = new Intl.Collator(undefined, { numeric: true });
    return {
      values: [...values].sort((a, b) => collator.compare(a, b)),
      isLoading: local.isLoading || queries.isLoading,
    };
  }, [local, queries]);
}

/** A Workbook Report's value-list filter control, over {@link useWorkbookFilterOptions}. */
export function WorkbookValuesControl(props: ReportValuesControlProps) {
  const options = useWorkbookFilterOptions(props.filter.column);
  return <ReportValuesSelect {...props} options={options} />;
}
